import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUsersData } from "./useUsersData";
import { useMaterialFilters } from "./useMaterialFilters";
import { useRequestStatuses } from "./useRequestStatuses";
import { useSavedIds } from "./useSavedIds";
import { apiPostJson } from "../api/client";
import { useDataCache, CACHE_KEYS } from "../contexts/DataCacheContext";
import { FALLBACK_CENTER_LAT, FALLBACK_CENTER_LON, computeZoomForRadius } from "../utils/mapUtils";

const PAGE_SIZE = 24;
const MAP_PAGE_SIZE = 100;

const EMPTY_FILTERS = {
  categories: [],
  materials: [],
  quantityMin: "",
  quantityMax: "",
  costMin: "",
  costMax: "",
  pickupLatestBy: "",
  pickupTimeFrom: "",
  pickupTimeTo: "",
  currentLocation: "",
  radiusKm: 2,
  locationCoords: null,
  weekdays: [],
  deliveryMethods: ["pickup", "delivery"],
};

export function useMaterialManagementController() {
  // data hooks
  const { users, loading: usersLoading, errorMessage: usersErr } = useUsersData();
  const {
    materialTypes: backendMaterialTypes,
    materialCategories: backendMaterialCategories,
    loading: filtersLoading,
    errorMessage: filtersErr,
  } = useMaterialFilters();
  const {
    pendingListingIds,
    approvedListingIds,
    error: requestsErr,
    markPendingOptimistic,
    unmarkPending,
  } = useRequestStatuses();

  // bookmarks
  const { ids: savedIds, toggle: toggleSave, count: savedCount } = useSavedIds();

  // cache
  const { getCachedData, setCachedData } = useDataCache();
  const getCacheRef = useRef(getCachedData);
  const setCacheRef = useRef(setCachedData);
  useEffect(() => { getCacheRef.current = getCachedData; }, [getCachedData]);
  useEffect(() => { setCacheRef.current = setCachedData; }, [setCachedData]);

  // search state
  const [hasSearched, setHasSearched] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsErr, setResultsErr] = useState("");
  const [serverListings, setServerListings] = useState([]);

  // pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // view + sort
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("newest");

  // filters
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // timestamps
  const [lastFetched, setLastFetched] = useState(null);

  // receiver coords from backend
  const [receiverCoords, setReceiverCoords] = useState(null);

  // map state
  const [selectedCompany, setSelectedCompany] = useState(null);

  // detail modal
  const [detailListing, setDetailListing] = useState(null);

  // request modal
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestListing, setRequestListing] = useState(null);

  // derived
  const metaLoading = usersLoading || filtersLoading;
  const errorMessage = usersErr || filtersErr || requestsErr || resultsErr;

  const usersById = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(users)) return map;
    for (const u of users) {
      const id = u?.id ?? u?.user_id;
      if (id != null) map.set(id, u);
    }
    return map;
  }, [users]);

  // active filter count — includes categories
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.categories?.length) n++;
    if (filters.materials.length) n++;
    if (filters.quantityMin?.trim()) n++;
    if (filters.quantityMax?.trim() && filters.quantityMax !== "1000") n++;
    if (filters.costMin?.trim()) n++;
    if (filters.costMax?.trim() && filters.costMax !== "25") n++;
    if (filters.pickupLatestBy?.trim()) n++;
    if (filters.pickupTimeFrom?.trim()) n++;
    if (filters.pickupTimeTo?.trim()) n++;
    if (filters.currentLocation?.trim()) n++;
    if (filters.weekdays.length) n++;
    if (Array.isArray(filters.deliveryMethods) && filters.deliveryMethods.length === 1) n++;
    return n;
  }, [filters]);

  // build search payload
  const buildPayload = useCallback((pageNum, sort, filts, pageSize = PAGE_SIZE) => {
    const p = { page: pageNum, page_size: pageSize, sort_by: sort };

    if (filts.categories.length) p.categories = filts.categories;
    if (filts.materials.length) p.materials = filts.materials;
    if (filts.quantityMin?.trim()) p.quantity_min = Number(filts.quantityMin);
    if (filts.quantityMax?.trim()) p.quantity_max = Number(filts.quantityMax);
    if (filts.costMin?.trim()) p.cost_min = Number(filts.costMin);
    if (filts.costMax?.trim()) p.cost_max = Number(filts.costMax);
    if (filts.pickupLatestBy?.trim()) p.pickup_latest_by = filts.pickupLatestBy;
    if (filts.pickupTimeFrom?.trim()) p.pickup_time_from = filts.pickupTimeFrom;
    if (filts.pickupTimeTo?.trim()) p.pickup_time_to = filts.pickupTimeTo;
    if (filts.currentLocation?.trim()) p.current_location = filts.currentLocation;
    if (Number.isFinite(filts.radiusKm)) p.radius_km = filts.radiusKm;
    if (filts.weekdays.length) p.weekdays = filts.weekdays;
    // only filter when one method selected (both = no filter)
    if (Array.isArray(filts.deliveryMethods) && filts.deliveryMethods.length === 1) {
      p.delivery_method = filts.deliveryMethods[0];
    }

    return p;
  }, []);

  // apply cached or fresh results to state
  const applyResults = useCallback((res, ts) => {
    const items = res?.items ?? [];
    setServerListings(Array.isArray(items) ? items : []);
    setLastFetched(ts);

    if (res?.pagination) {
      setTotalPages(res.pagination.total_pages || 1);
      setTotal(res.pagination.total || 0);
      setPage(res.pagination.page || 1);
    }

    if (res?.receiver?.lat != null && res?.receiver?.lon != null) {
      setReceiverCoords({ lat: res.receiver.lat, lon: res.receiver.lon });
    }
  }, []);

  // core search — supports cache
  const search = useCallback(async (pageNum, sort, filts, forceRefresh = false, pageSize = PAGE_SIZE) => {
    const params = buildPayload(pageNum, sort, filts, pageSize);

    // try cache on initial load (page 1, default sort, no filters, not forced)
    if (!forceRefresh && pageNum === 1) {
      const cached = getCacheRef.current?.(CACHE_KEYS.BROWSE_MATERIALS);
      const cachedData = cached?.data ?? cached;
      const cachedTs = cached?.timestamp ?? 0;

      if (cachedData?.items && Array.isArray(cachedData.items) && cachedData.items.length > 0 && cachedTs) {
        setHasSearched(true);
        applyResults(cachedData, cachedTs);
        setResultsLoading(false);
        return;
      }
    }

    setResultsLoading(true);
    setResultsErr("");
    setHasSearched(true);

    try {
      const res = await apiPostJson("/material_management/search", params);
      const ts = Date.now();
      applyResults(res, ts);

      // cache the first page of default results
      if (pageNum === 1) {
        setCacheRef.current?.(CACHE_KEYS.BROWSE_MATERIALS, res, ts);
      }
    } catch (e) {
      setServerListings([]);
      setResultsErr(e?.message || "Search failed");
    } finally {
      setResultsLoading(false);
    }
  }, [buildPayload, applyResults]);

  // refresh always skips cache
  const refresh = useCallback(() => search(page, sortBy, filters, true), [search, page, sortBy, filters]);

  // switching views re-searches with the right page size
  const changeViewMode = useCallback((mode) => {
    setViewMode(mode);
    const ps = mode === "map" ? MAP_PAGE_SIZE : PAGE_SIZE;
    search(1, sortBy, filters, true, ps);
  }, [sortBy, filters, search]);

  const didMount = useRef(false);

  useEffect(() => {
    if (!metaLoading && !didMount.current) {
      didMount.current = true;
      search(1, sortBy, filters);
    }
  }, [metaLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCategory = useCallback((cats) => {
    const next = { ...filters, categories: cats };
    setFilters(next);
    setPage(1);
    search(1, sortBy, next, true);
  }, [filters, sortBy, search]);

  const changeSort = useCallback((s) => {
    setSortBy(s);
    setPage(1);
    search(1, s, filters, true);
  }, [filters, search]);

  const goToPage = useCallback((p) => {
    setPage(p);
    search(p, sortBy, filters, true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [sortBy, filters, search]);

  const applyDrawerFilters = useCallback((drawerFilters) => {
    const next = { ...filters, ...drawerFilters };
    setFilters(next);
    setIsDrawerOpen(false);
    setPage(1);
    search(1, sortBy, next, true);
  }, [filters, sortBy, search]);

  const resetFilters = useCallback(() => {
    const next = { ...EMPTY_FILTERS };
    setFilters(next);
    setIsDrawerOpen(false);
    setPage(1);
    search(1, sortBy, next, true);
  }, [sortBy, search]);

  // remove specific filter keys and re-search
  const removeFilter = useCallback((keys) => {
    const next = { ...filters };
    for (const k of keys) next[k] = EMPTY_FILTERS[k];
    setFilters(next);
    setPage(1);
    search(1, sortBy, next, true);
  }, [filters, sortBy, search]);

  // decorate listings
  const listings = useMemo(() => {
    return (serverListings || []).map((l) => {
      const id = String(l.id ?? l.listing_id ?? "");
      const companyId = l.company_id ?? l.companyId;
      const user = companyId ? usersById.get(companyId) : null;

      return {
        ...l,
        companyId,
        companyName: user?.company_name ?? user?.companyName ?? user?.name ?? "—",
        requestStatus: id && approvedListingIds.has(id)
          ? "approved"
          : id && pendingListingIds.has(id)
          ? "pending"
          : null,
      };
    });
  }, [serverListings, usersById, approvedListingIds, pendingListingIds]);

  // map markers — one per company, includes all geocoded users
  const markers = useMemo(() => {
    if (!users?.length) return [];

    // count listings per company
    const listingCounts = new Map();
    const materialsByCompany = new Map();
    for (const l of listings) {
      const cid = l.companyId;
      if (!cid) continue;
      listingCounts.set(cid, (listingCounts.get(cid) || 0) + 1);
      const mat = l.material || l.materialType || l.material_name;
      if (!mat) continue;
      if (!materialsByCompany.has(cid)) materialsByCompany.set(cid, new Set());
      materialsByCompany.get(cid).add(String(mat));
    }

    // build markers from all geocoded users
    const result = [];
    for (const u of users) {
      const uid = String(u.id ?? u.user_id ?? "");
      if (!uid) continue;

      const lat = typeof u.lat === "number" ? u.lat : Number(u.lat);
      const lon = typeof u.lon === "number" ? u.lon : Number(u.lon ?? u.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;

      result.push({
        companyId: uid,
        position: { lat, lng: lon },
        companyName: u.company_name ?? u.companyName ?? "",
        address: u.address ?? "",
        materials: Array.from(materialsByCompany.get(uid) || []).sort(),
        listingCount: listingCounts.get(uid) || 0,
      });
    }

    return result;
  }, [listings, users]);

  // map center — selected filter location → receiver coords → first marker → fallback
  const mapCenter = useMemo(() => {
    if (filters.locationCoords?.lat != null && filters.locationCoords?.lon != null) {
      return { lat: filters.locationCoords.lat, lng: filters.locationCoords.lon };
    }
    if (receiverCoords) return { lat: receiverCoords.lat, lng: receiverCoords.lon };
    if (markers.length) return markers[0].position;
    return { lat: FALLBACK_CENTER_LAT, lng: FALLBACK_CENTER_LON };
  }, [filters.locationCoords, receiverCoords, markers]);

  const mapZoom = useMemo(() => {
    return computeZoomForRadius(filters.radiusKm || 100, mapCenter.lat);
  }, [filters.radiusKm, mapCenter.lat]);

  // map marker click — store the user record for the side panel
  const handleMarkerClick = useCallback((marker) => {
    const u = marker.companyId ? usersById.get(marker.companyId) : null;
    setSelectedCompany(u || null);
  }, [usersById]);

  // all listings belonging to the selected company
  const companyListings = useMemo(() => {
    if (!selectedCompany) return [];
    const cid = String(selectedCompany.id ?? selectedCompany.user_id ?? "");
    return listings.filter((l) => String(l.companyId) === cid);
  }, [selectedCompany, listings]);

  // bridge: map panel listing click → detail panel
  const selectMapListing = useCallback((listing) => {
    setDetailListing(listing);
  }, []);

  // detail modal
  const openDetail = useCallback((listing) => setDetailListing(listing), []);
  const closeDetail = useCallback(() => setDetailListing(null), []);

  // request modal
  const openRequest = useCallback((listing) => {
    if (!listing) return;
    setRequestListing(listing);
    setRequestModalOpen(true);
  }, []);

  const requestCompanyUser = useMemo(() => {
    const companyId = requestListing?.company_id ?? requestListing?.companyId;
    return companyId ? usersById.get(companyId) : null;
  }, [requestListing, usersById]);

  return {
    metaLoading,
    errorMessage,
    users,
    usersById,
    materialTypesForFilters: backendMaterialTypes,
    backendMaterialCategories,

    hasSearched,
    resultsLoading,
    listings,
    refresh,
    lastFetched,

    page,
    totalPages,
    total,
    goToPage,

    viewMode,
    setViewMode: changeViewMode,
    sortBy,
    changeSort,

    filters,
    setCategory,
    applyDrawerFilters,
    resetFilters,
    removeFilter,
    activeFilterCount,
    isDrawerOpen,
    setIsDrawerOpen,

    receiverCoords,

    // effective origin for distance calcs — selected filter location or receiver home
    effectiveCoords: filters.locationCoords ?? receiverCoords,

    // map
    markers,
    mapCenter,
    mapZoom,
    selectedCompany,
    setSelectedCompany,
    clearSelectedCompany: () => setSelectedCompany(null),
    handleMarkerClick,
    companyListings,
    selectMapListing,

    // detail modal
    detailListing,
    openDetail,
    closeDetail,

    savedIds,
    toggleSave,
    savedCount,

    requestModalOpen,
    setRequestModalOpen,
    requestListing,
    requestCompanyUser,
    openRequest,

    markPendingOptimistic,
    unmarkPending,
  };
}
